import {
  type AwardKind,
  type BodyMeasurementSource,
  COOKING_OIL_PER_100G,
  type CookedResult,
  type CookingMethod,
  deviceTimeZone,
  type FoodUsage,
  localDateString,
  type MacroTotals,
  type MealSlot,
  type NullableNutrient,
  type Per100g,
  rankFoods,
  recallStems,
  recipePer100gCooked,
  scaleToGrams,
  sumKnown,
  undeclaredNutrients,
  WATER_NAME,
  withCookingOil,
} from '@gramas/shared'
import Dexie, { type IndexableType } from 'dexie'
import {
  db,
  type FavouriteKind,
  type LocalAward,
  type LocalBarcode,
  type LocalBodyMeasurement,
  type LocalFavourite,
  type LocalFood,
  type LocalGoal,
  type LocalLogEntry,
  type LocalRecipe,
  type LocalRecipeIngredient,
  type LocalUsage,
  type LocalWorkout,
  refKey,
  searchKeyFor,
} from './dexie.js'

const now = () => new Date().toISOString()
const newId = () => crypto.randomUUID()

/** `undefined` leaves a stored value alone; an explicit `null` clears it. */
function patch<T>(incoming: T | null | undefined, stored: T | null | undefined): T | null {
  return incoming === undefined ? (stored ?? null) : incoming
}

export function foodPer100g(food: LocalFood | LocalRecipe): Per100g {
  return {
    kcal: food.kcal,
    protein: food.protein,
    carbs: food.carbs,
    fat: food.fat,
    sugars: food.sugars,
    satFat: food.satFat,
    fiber: food.fiber,
    salt: food.salt,
  }
}

export function foodDisplayName(food: Pick<LocalFood, 'nameLt' | 'nameEn' | 'brand'>): string {
  const base = food.nameLt ?? food.nameEn ?? 'Be pavadinimo'
  return food.brand ? `${food.brand} ${base}` : base
}

// ─── Foods ───────────────────────────────────────────────────────────────────

export interface FoodDraft {
  id?: string
  nameLt?: string | null
  nameEn?: string | null
  brand?: string | null
  servingSizeG?: number | null
  servingName?: string | null
  pieceWeightG?: number | null
  unit?: 'g' | 'ml'
  visibility?: 'public' | 'private'
  isVerified?: boolean
}

export async function saveFood(draft: FoodDraft, nutrients: Per100g): Promise<LocalFood> {
  const id = draft.id ?? newId()
  const food: LocalFood = {
    id,
    nameLt: draft.nameLt ?? null,
    nameEn: draft.nameEn ?? null,
    brand: draft.brand ?? null,
    ...nutrients,
    sugars: nutrients.sugars ?? null,
    satFat: nutrients.satFat ?? null,
    fiber: nutrients.fiber ?? null,
    salt: nutrients.salt ?? null,
    servingSizeG: draft.servingSizeG ?? null,
    servingName: draft.servingName ?? null,
    pieceWeightG: draft.pieceWeightG ?? null,
    unit: draft.unit ?? 'g',
    isVerified: draft.isVerified ?? false,
    // Public by default: this app has two accounts sharing one catalogue, and
    // a product either of them has already typed in is not worth the other
    // typing again. A private food is now the deliberate case, not the default.
    visibility: draft.visibility ?? 'public',
    searchKey: searchKeyFor(draft.brand, draft.nameLt, draft.nameEn),
    updatedAt: now(),
    deletedAt: null,
    // The user's own product, not cache: no expiry clock, never evicted.
    cachedAt: null,
    pinned: 1,
    dirty: 1,
  }
  await db.foods.put(food)
  return food
}

/**
 * Remember a food the server handed back — a resolved scan, or a search result
 * the user picked. This is the only way a catalog row enters the phone, which
 * is what keeps the local database a few megabytes while the server holds tens
 * of thousands of products.
 */
export async function cacheFood(
  food: Omit<LocalFood, 'searchKey' | 'cachedAt' | 'pinned' | 'dirty'>,
): Promise<LocalFood> {
  const existing = await db.foods.get(food.id)
  // An unsent local edit outranks the server's copy; push resolves it, and
  // overwriting it here would throw away what the user typed.
  if (existing?.dirty === 1) return existing

  const row: LocalFood = {
    ...food,
    searchKey: searchKeyFor(food.brand, food.nameLt, food.nameEn),
    // A row the user owns stays theirs — giving it a cachedAt would put their
    // own product on the eviction clock.
    cachedAt: existing != null && existing.cachedAt == null ? null : now(),
    pinned: existing?.pinned ?? 0,
    dirty: 0,
  }
  await db.foods.put(row)
  return row
}

/**
 * What this phone has eaten, keyed by food id.
 *
 * Two sources, because neither is complete on its own. `db.usage` is a local
 * counter and a fresh install starts it at zero; the diary is synced and knows
 * everything, but only as far back as the phone has pulled. The larger of the
 * two is the honest answer.
 *
 * How recently each food was eaten travels with the counts, because the ranker
 * spends part of the familiarity bonus on it: a year of diary makes a great
 * many foods equally familiar, and what somebody ate this week is the better
 * guess at what they are typing now.
 *
 * Cached briefly rather than recomputed per keystroke: the diary does not
 * change while somebody is typing a search, and fifteen seconds of staleness
 * costs at most one food ranking as though it had been eaten once less.
 */
const USAGE_TTL_MS = 15_000
let usageCache: { at: number; usage: Map<string, FoodUsage> } | null = null

export async function foodUsage(): Promise<ReadonlyMap<string, FoodUsage>> {
  const cached = usageCache
  if (cached && Date.now() - cached.at < USAGE_TTL_MS) return cached.usage

  const usage = new Map<string, FoodUsage>()
  const bump = (id: string, times: number) => {
    const row = usage.get(id)
    if (row == null) usage.set(id, { timesLogged: times })
    else row.timesLogged = Math.max(row.timesLogged ?? 0, times)
  }

  // Tracked apart from the counts and maxed independently. The two sources
  // disagree about both numbers, and carrying the timestamp of whichever count
  // happened to win would hand a phone that has just pulled a year of history
  // the local counter's stale date.
  const lastAt = new Map<string, number>()
  const seenAt = (id: string, at: number) => {
    if (Number.isFinite(at) && at > (lastAt.get(id) ?? 0)) lastAt.set(id, at)
  }

  const counted = new Map<string, number>()
  await db.logEntries.each((entry) => {
    if (entry.deletedAt != null || entry.foodId == null) return
    counted.set(entry.foodId, (counted.get(entry.foodId) ?? 0) + 1)
    // An entry written for a past day carries the day and no clock time; noon
    // is the least wrong hour to put it at, and the curve is measured in days.
    seenAt(entry.foodId, Date.parse(entry.loggedAt ?? `${entry.logDate}T12:00:00.000Z`))
  })
  for (const [id, times] of counted) bump(id, times)
  await db.usage.each((row) => {
    if (row.kind !== 'food') return
    bump(row.refId, row.count)
    seenAt(row.refId, Date.parse(row.lastUsedAt))
  })
  for (const [id, at] of lastAt) {
    const row = usage.get(id)
    if (row) row.lastUsedAt = new Date(at).toISOString()
  }
  for (const key of await db.favourites.orderBy('foodId').keys()) {
    const id = String(key)
    usage.set(id, { ...usage.get(id), favourite: true })
  }

  usageCache = { at: Date.now(), usage }
  return usage
}

/** Forget the cached counts. Called wherever one of them changes. */
export function forgetFoodUsage(): void {
  usageCache = null
}

/**
 * The cached slice of the catalogue, ranked by the same scorer the server uses.
 *
 * Recall is deliberately loose — any one of the query's stems appearing
 * anywhere in the row is enough to be looked at — because the ranking is what
 * decides the order, and a row excluded here can never be found however well it
 * would have scored. The old version asked the opposite: it required every
 * typed word as a literal substring, which missed "Varškė" for "varškės", and
 * then stopped scanning at the first `limit` rows in alphabetical order, so the
 * best match was frequently never even visited.
 */
export async function searchFoodsLocal(query: string, limit = 25): Promise<LocalFood[]> {
  const stems = recallStems(query)
  if (stems.length === 0) return []

  const candidates: LocalFood[] = []
  await db.foods
    .filter((food) => food.deletedAt == null && stems.some((s) => food.searchKey.includes(s)))
    .each((food) => {
      candidates.push(food)
    })

  const usage = await foodUsage()
  return rankFoods(query, candidates, { usage })
    .slice(0, limit)
    .map((match) => match.food)
}

// ─── Food cache: pinning and eviction ────────────────────────────────────────

/** Roughly a season without being opened; after that a cached row is dead weight. */
export const FOOD_CACHE_MAX_AGE_DAYS = 90

/** Enough for years of ordinary shopping, small enough to never be noticed. */
export const FOOD_CACHE_MAX_ROWS = 3000

const DAY_MS = 86_400_000

/**
 * Re-derive which cached foods may not be evicted.
 *
 * Pins are never trusted to stay correct on their own: entries get deleted,
 * recipes get rewritten, favourites get removed, and a pin left behind by any
 * of those would keep a row alive forever. So the set is rebuilt from the
 * user's own data every time the cache is pruned.
 */
async function recomputeFoodPins(): Promise<void> {
  const wanted = new Set<string>()

  await db.logEntries.each((entry) => {
    if (entry.foodId != null && entry.deletedAt == null) wanted.add(entry.foodId)
  })
  await db.recipes.each((recipe) => {
    if (recipe.deletedAt != null) return
    for (const ingredient of recipe.ingredients) wanted.add(ingredient.foodId)
  })
  // A null index key is absent from an IndexedDB index, so ordering by these
  // two visits only the favourited and only the personally scanned rows.
  for (const foodId of await db.favourites.orderBy('foodId').keys()) {
    wanted.add(String(foodId))
  }
  await db.barcodes
    .orderBy('scannedAt')
    .filter((binding) => binding.deletedAt == null)
    .each((binding) => wanted.add(binding.foodId))

  const changed: LocalFood[] = []
  // Walk the pinned rows rather than the whole table: the pinned set is bounded
  // by how much the user has logged, the cache is bounded by nothing.
  await db.foods
    .where('pinned')
    .equals(1)
    .each((food) => {
      const own = food.cachedAt == null || food.dirty === 1
      if (own || wanted.has(food.id)) return
      changed.push({ ...food, pinned: 0 })
    })
  for (const food of await db.foods.bulkGet([...wanted])) {
    if (food && food.pinned === 0) changed.push({ ...food, pinned: 1 })
  }
  await db.foods.bulkPut(changed)
}

export interface CachePruneOptions {
  maxAgeDays?: number
  maxRows?: number
}

export interface CachePruneResult {
  evicted: number
  barcodesEvicted: number
}

/**
 * Drop what the phone no longer needs to hold.
 *
 * Two rules, because either alone leaves a hole: an age limit lets a cache that
 * is never used shrink back to nothing, and a row cap stops a month of heavy
 * searching from filling the device before anything is old enough to expire.
 */
export async function pruneFoodCache(options: CachePruneOptions = {}): Promise<CachePruneResult> {
  const maxAgeDays = options.maxAgeDays ?? FOOD_CACHE_MAX_AGE_DAYS
  const maxRows = options.maxRows ?? FOOD_CACHE_MAX_ROWS

  await recomputeFoodPins()

  const cutoff = new Date(Date.now() - maxAgeDays * DAY_MS).toISOString()
  // The user's own foods carry a null cachedAt, which leaves them out of this
  // compound index altogether, so nothing here can reach them.
  const range = (from: IndexableType, to: IndexableType) =>
    db.foods.where('[pinned+cachedAt]').between([0, from], [0, to], true, false)

  const stale = await range(Dexie.minKey, cutoff).primaryKeys()
  // primaryKeys() comes back in index order, so the survivors are already
  // least-recently-used first and the overflow slice needs no sorting.
  const surviving = await range(cutoff, Dexie.maxKey).primaryKeys()
  const doomed = [...stale, ...surviving.slice(0, Math.max(0, surviving.length - maxRows))]
  if (doomed.length === 0) return { evicted: 0, barcodesEvicted: 0 }

  // A binding this device scanned is kept whatever happens to the food behind
  // it: re-scanning the same jar offline has to keep working, and one row per
  // scan is a rounding error next to the catalog it replaces.
  const orphaned = await db.barcodes
    .where('foodId')
    .anyOf(doomed)
    .filter((binding) => binding.dirty === 0 && binding.scannedAt == null)
    .toArray()

  await db.transaction('rw', [db.foods, db.barcodes, db.usage], async () => {
    await db.foods.bulkDelete(doomed)
    await db.barcodes.bulkDelete(orphaned.map((binding) => binding.code))
    await db.usage.bulkDelete(doomed.map((id) => refKey('food', id)))
  })

  return { evicted: doomed.length, barcodesEvicted: orphaned.length }
}

// ─── Barcodes ────────────────────────────────────────────────────────────────

export async function findFoodByBarcode(code: string): Promise<LocalFood | null> {
  const binding = await db.barcodes.get(code)
  if (!binding || binding.deletedAt != null) return null
  const food = await db.foods.get(binding.foodId)
  return food && food.deletedAt == null ? food : null
}

export async function bindBarcode(
  code: string,
  symbology: string,
  foodId: string,
  fromServer = false,
): Promise<LocalBarcode> {
  // Every call here is a code this device pointed a camera at — sync writes the
  // server's bindings straight to the table — so the scan stamp goes on
  // unconditionally, and `fromServer` only decides whether to push it back.
  const binding: LocalBarcode = {
    code,
    symbology,
    foodId,
    boundAt: now(),
    scannedAt: now(),
    deletedAt: null,
    dirty: fromServer ? 0 : 1,
  }
  await db.barcodes.put(binding)
  return binding
}

/**
 * The scan → OCR → name → save loop, committed as one unit: a food that exists
 * without its barcode binding would send the user through OCR again next time.
 */
export async function saveFoodWithBarcode(
  draft: FoodDraft,
  nutrients: Per100g,
  barcode: { code: string; symbology: string } | null,
): Promise<LocalFood> {
  return db.transaction('rw', db.foods, db.barcodes, async () => {
    const food = await saveFood(draft, nutrients)
    if (barcode) await bindBarcode(barcode.code, barcode.symbology, food.id)
    return food
  })
}

// ─── Diary ───────────────────────────────────────────────────────────────────

export interface LogInput {
  slot: MealSlot
  quantityG: number
  servings?: number | null
  notes?: string | null
  logDate?: string
}

function entryFrom(
  source: { id: string; kind: 'food' | 'recipe' | 'build'; name: string; per100g: Per100g },
  input: LogInput,
): LocalLogEntry {
  const tz = deviceTimeZone()
  const totals = scaleToGrams(source.per100g, input.quantityG)
  return {
    id: newId(),
    logDate: input.logDate ?? localDateString(new Date(), tz),
    loggedAt: now(),
    loggedTz: tz,
    slot: input.slot,
    foodId: source.kind === 'food' ? source.id : null,
    recipeId: source.kind === 'recipe' ? source.id : null,
    fastfoodBuildId: source.kind === 'build' ? source.id : null,
    quantityG: input.quantityG,
    servings: input.servings ?? null,
    snapshotName: source.name,
    ...totals,
    notes: input.notes ?? null,
    clientEditedAt: now(),
    deletedAt: null,
    dirty: 1,
  }
}

/**
 * Log a food. The macros are copied onto the entry rather than referenced:
 * correcting a food's label later must not silently rewrite what was eaten
 * last week.
 */
export async function logFood(food: LocalFood, input: LogInput): Promise<LocalLogEntry> {
  const entry = entryFrom(
    { id: food.id, kind: 'food', name: foodDisplayName(food), per100g: foodPer100g(food) },
    input,
  )
  await db.transaction('rw', [db.logEntries, db.foods, db.usage], async () => {
    await db.logEntries.put(entry)
    // The food may have come straight off a server search and never been
    // stored. Logging it is the use that earns it a place on the phone, and
    // the entry pins it: a diary that cannot re-open what it recorded is worse
    // than one that keeps a few kilobytes too many.
    const stored = await db.foods.get(food.id)
    if (!stored) await db.foods.put({ ...food, cachedAt: now(), pinned: 1 })
    else if (stored.cachedAt == null) await db.foods.update(food.id, { pinned: 1 })
    else await db.foods.update(food.id, { pinned: 1, cachedAt: now() })
    await recordUsage('food', food.id)
  })
  return entry
}

export async function logRecipe(recipe: LocalRecipe, input: LogInput): Promise<LocalLogEntry> {
  const entry = entryFrom(
    { id: recipe.id, kind: 'recipe', name: recipe.name, per100g: foodPer100g(recipe) },
    input,
  )
  await db.transaction('rw', [db.logEntries, db.usage], async () => {
    await db.logEntries.put(entry)
    await recordUsage('recipe', recipe.id)
  })
  return entry
}

/** Free-form entry with no catalog row behind it (voice log, quick estimate). */
export async function logAdHoc(
  name: string,
  totals: MacroTotals,
  input: LogInput,
): Promise<LocalLogEntry> {
  const tz = deviceTimeZone()
  const entry: LocalLogEntry = {
    id: newId(),
    logDate: input.logDate ?? localDateString(new Date(), tz),
    loggedAt: now(),
    loggedTz: tz,
    slot: input.slot,
    foodId: null,
    recipeId: null,
    fastfoodBuildId: null,
    quantityG: input.quantityG,
    servings: input.servings ?? null,
    snapshotName: name,
    ...totals,
    notes: input.notes ?? null,
    clientEditedAt: now(),
    deletedAt: null,
    dirty: 1,
  }
  await db.logEntries.put(entry)
  return entry
}

/**
 * The oil a cooking method put into the food, as its own diary row.
 *
 * Separate rather than folded into the food for two reasons: the food's
 * snapshot has to keep meaning exactly what the label says, and a line that
 * reads "Aliejus (keptuvėje) 8 g" is one you can delete on the day you used the
 * air fryer. Grams come from the cooking maths rather than being reconstructed
 * from the energy, so the two can never disagree about what an oil is.
 */
export async function logCookingOil(
  methodLabel: string,
  cooked: CookedResult,
  input: LogInput,
): Promise<void> {
  if (cooked.addedOilG <= 0) return
  await logAdHoc(
    `Aliejus (${methodLabel.toLowerCase()})`,
    scaleToGrams(COOKING_OIL_PER_100G, cooked.addedOilG),
    { ...input, quantityG: cooked.addedOilG },
  )
}

/**
 * Change how much of an entry was eaten, rescaling everything it carries.
 *
 * The snapshot is scaled rather than re-derived from the food, because the
 * entry is the record of what was eaten and the catalogue may have moved on.
 * `servings` scales with it: for a counter item the pair (quantityG, servings)
 * *is* the definition of a portion, and leaving the count behind made the next
 * edit read a portion as half of one.
 */
export async function updateEntryQuantity(id: string, quantityG: number): Promise<void> {
  const entry = await db.logEntries.get(id)
  if (!entry) return
  // Nothing can be scaled up from nothing, and dividing by it wrote NaN into
  // the diary — which then spread through the day's totals and synced.
  if (!(entry.quantityG > 0) || !(quantityG > 0)) return
  const scale = quantityG / entry.quantityG
  await db.logEntries.put({
    ...entry,
    quantityG,
    servings: entry.servings == null ? null : Math.round(entry.servings * scale * 100) / 100,
    kcal: Math.round(entry.kcal * scale * 100) / 100,
    protein: Math.round(entry.protein * scale * 100) / 100,
    carbs: Math.round(entry.carbs * scale * 100) / 100,
    fat: Math.round(entry.fat * scale * 100) / 100,
    sugars: entry.sugars == null ? null : Math.round(entry.sugars * scale * 100) / 100,
    satFat: entry.satFat == null ? null : Math.round(entry.satFat * scale * 100) / 100,
    fiber: entry.fiber == null ? null : Math.round(entry.fiber * scale * 100) / 100,
    salt: entry.salt == null ? null : Math.round(entry.salt * scale * 1000) / 1000,
    clientEditedAt: now(),
    dirty: 1,
  })
}

/**
 * Log the same thing again, today.
 *
 * The quick-add lists are built from `db.usage`, which only knows about foods
 * and recipes — so a Circle K latte with two sugars, a voice log, or anything
 * else assembled rather than looked up could be logged once and never repeated
 * without rebuilding it part by part. Copying the entry's own snapshot avoids
 * inventing a catalogue row for something that never had one, and it repeats
 * what was actually eaten rather than what the product says today.
 */
export async function repeatEntry(
  id: string,
  input: { slot?: MealSlot; logDate?: string } = {},
): Promise<LocalLogEntry | null> {
  const entry = await db.logEntries.get(id)
  if (!entry) return null
  const tz = deviceTimeZone()
  const copy: LocalLogEntry = {
    ...entry,
    id: newId(),
    logDate: input.logDate ?? localDateString(new Date(), tz),
    loggedAt: now(),
    loggedTz: tz,
    // The same rule the original log went through, applied to the name that was
    // stored on it: repeating the latte this exists to repeat used to file it
    // under lunch, which is exactly what the always-a-snack rule prevents.
    slot: input.slot ?? slotFor(null, null, entry.snapshotName),
    clientEditedAt: now(),
    deletedAt: null,
    dirty: 1,
  }
  await db.logEntries.put(copy)
  return copy
}

/** Soft delete so the tombstone can reach the other device. */
export async function deleteEntry(id: string): Promise<void> {
  const entry = await db.logEntries.get(id)
  if (!entry) return
  await db.logEntries.put({ ...entry, deletedAt: now(), clientEditedAt: now(), dirty: 1 })
}

/**
 * Undo for a just-deleted row. Clearing the tombstone is one more LWW edit on
 * the same id, so it wins over the delete on both devices and keeps the row's
 * food link intact — re-creating the entry would lose it.
 */
export async function restoreEntry(id: string): Promise<void> {
  const entry = await db.logEntries.get(id)
  if (!entry) return
  await db.logEntries.put({ ...entry, deletedAt: null, clientEditedAt: now(), dirty: 1 })
}

export async function entriesForDay(date: string): Promise<LocalLogEntry[]> {
  const rows = await db.logEntries.where('logDate').equals(date).toArray()
  return rows.filter((e) => e.deletedAt == null)
}

export function totalsFor(entries: readonly LocalLogEntry[]): MacroTotals {
  if (entries.length === 0) {
    return { kcal: 0, protein: 0, carbs: 0, fat: 0, sugars: 0, satFat: 0, fiber: 0, salt: 0 }
  }
  // sumKnown, not sumTotals: a day is a floor made of what the labels said, not
  // an unknown because one of them was silent. See sumKnown, and pair it with
  // `undeclaredNutrients` wherever the number is shown.
  return sumKnown(entries)
}

/**
 * Yesterday's breakfast, again.
 *
 * Most people eat the same first meal for months, and rebuilding it item by
 * item every morning is the tax that makes food diaries get abandoned. Each row
 * is copied through `repeatEntry`, so what lands is the snapshot that was
 * actually eaten rather than what the catalogue says about it today.
 */
export async function copyMeal(input: {
  from: string
  to: string
  slot: MealSlot
}): Promise<number> {
  const source = (await entriesForDay(input.from)).filter((e) => e.slot === input.slot)
  for (const entry of source) {
    await repeatEntry(entry.id, { slot: input.slot, logDate: input.to })
  }
  return source.length
}

/**
 * Put an entry in the meal or on the day it actually belonged to.
 *
 * The alternative was deleting it and logging it again, which loses the food it
 * was logged from and the snapshot of what that food said at the time — so a
 * lunch mis-filed as a snack could only be corrected by throwing away the
 * record of it.
 */
export async function moveEntry(
  id: string,
  input: { slot?: MealSlot; logDate?: string },
): Promise<void> {
  const entry = await db.logEntries.get(id)
  if (!entry) return
  await db.logEntries.put({
    ...entry,
    slot: input.slot ?? entry.slot,
    logDate: input.logDate ?? entry.logDate,
    clientEditedAt: now(),
    dirty: 1,
  })
}

/**
 * Mean kcal across the days in the window that were logged at all.
 *
 * Unlogged days are left out of both halves: a day with nothing in it is a day
 * nobody wrote down, not a day of eating nothing, and averaging it in as a zero
 * would make every gap look like a fast.
 */
export async function intakeStats(
  from: string,
  to: string,
): Promise<{ meanKcal: number | null; loggedDays: number }> {
  const rows = await db.logEntries.where('logDate').between(from, to, true, true).toArray()
  const byDate = new Map<string, number>()
  for (const row of rows) {
    if (row.deletedAt != null) continue
    byDate.set(row.logDate, (byDate.get(row.logDate) ?? 0) + row.kcal)
  }
  const days = [...byDate.values()].filter((kcal) => kcal > 0)
  if (days.length === 0) return { meanKcal: null, loggedDays: 0 }
  return {
    meanKcal: days.reduce((sum, kcal) => sum + kcal, 0) / days.length,
    loggedDays: days.length,
  }
}

export const SLOT_ORDER: readonly MealSlot[] = ['breakfast', 'lunch', 'dinner', 'snack']

export const SLOT_LABEL: Record<MealSlot, string> = {
  breakfast: 'Pusryčiai',
  lunch: 'Pietūs',
  dinner: 'Vakarienė',
  snack: 'Užkandžiai',
}

/** Meal the user is most likely logging right now, used to preselect the slot. */
export function slotForHour(hour: number = new Date().getHours()): MealSlot {
  if (hour < 11) return 'breakfast'
  if (hour < 15) return 'lunch'
  if (hour < 21) return 'dinner'
  return 'snack'
}

/**
 * Things that are a snack whatever time it is.
 *
 * A coffee at one o'clock is not lunch, and a slice of cake after dinner is not
 * dinner. Filing them by the clock puts them in the meal they were drunk beside
 * and makes every meal total wrong in the same direction, so these override the
 * hour outright. Everything else is a meal, and the clock is the better guess.
 */
const ALWAYS_SNACK =
  /(kav|latte|espres|capp?u|kapu[čc]|americ|moka|arbat|čaj|chai|matcha|gėrim|gerim|sultys|limonad|kokteil|smūt|smut|desert|tortas|pyrag|saldum|šokolad|sokolad|ledai|sausain|bandel|spurg|traškuč|traskuc|čips|cips|riešut|riesut|užkand|uzkand|batonėl|batonel|jogurt|snack|dessert|cake|candy|chocolate|crisps|chips|nuts)/i

/**
 * Which meal an entry belongs to, without asking.
 *
 * The slot is still stored and still editable — it is simply not a question
 * every log has to answer. Drinks and sweets are snacks by rule; anything else
 * falls to the hour, which is right often enough that correcting it is rarer
 * than confirming it would have been.
 */
export function slotFor(
  category: string | null | undefined,
  unit: 'g' | 'ml' | null | undefined,
  name?: string | null,
  hour: number = new Date().getHours(),
): MealSlot {
  if (unit === 'ml') return 'snack'
  const haystack = `${category ?? ''} ${name ?? ''}`
  if (haystack.trim() !== '' && ALWAYS_SNACK.test(haystack)) return 'snack'
  return slotForHour(hour)
}

// ─── Recipes ─────────────────────────────────────────────────────────────────

export interface RecipeDraft {
  id?: string
  name: string
  notes?: string | null
  cookedWeightG: number
  ingredients: Array<{
    id?: string
    foodId: string
    rawWeightG: number
    cooking?: CookingMethod | null
  }>
}

/**
 * The ingredients a recipe actually has.
 *
 * A recipe carries its ingredient list inline, so a removed one can only ever
 * show up as an absence — but a server used to send its tombstones down anyway,
 * and every phone that synced in that window wrote them into its own copy.
 * Reading the raw array shows the removed row again, and, worse, saves it back:
 * the editor hands its whole list to `saveRecipe`, which counts every entry
 * into the macros. Filter here, once, rather than at each of the four readers.
 */
export function liveIngredients(recipe: LocalRecipe): LocalRecipeIngredient[] {
  return recipe.ingredients.filter((i) => i.deletedAt == null)
}

export async function saveRecipe(draft: RecipeDraft): Promise<LocalRecipe> {
  // A weight of nothing is not an ingredient, and storing one is not a harmless
  // no-op: the wire schema requires a positive weight, so a single emptied
  // field would make every push from this phone fail from then on — and the
  // push runs before the pull.
  const incoming = draft.ingredients.filter((i) => i.rawWeightG > 0)
  const foods = await db.foods.bulkGet(incoming.map((i) => i.foodId))
  const resolved = incoming.flatMap((ing, idx) => {
    const food = foods[idx]
    return food
      ? [{ per100g: foodPer100g(food), rawWeightG: ing.rawWeightG, cooking: ing.cooking ?? null }]
      : []
  })
  if (resolved.length === 0) throw new Error('Receptui reikia bent vieno ingrediento')
  // An ingredient this phone does not hold cannot contribute its macros, and
  // dropping it quietly is how a recipe comes out lighter than the editor said
  // it was — permanently, because what gets logged from it is a snapshot. The
  // callers cache what they pick; this is the backstop that says so out loud.
  if (resolved.length !== incoming.length) {
    throw new Error('Trūksta ingrediento duomenų — pridėkite jį iš naujo')
  }

  // withCookingOil, not the bare list: the oil each pan left behind is part of
  // what is in the pot, and the server appends it the same way when it
  // recomputes — see recomputeRecipeMacros.
  const per100gCooked = recipePer100gCooked(withCookingOil(resolved), draft.cookedWeightG)
  const recipe: LocalRecipe = {
    id: draft.id ?? newId(),
    name: draft.name,
    notes: draft.notes ?? null,
    cookedWeightG: draft.cookedWeightG,
    ...per100gCooked,
    sugars: per100gCooked.sugars ?? null,
    satFat: per100gCooked.satFat ?? null,
    fiber: per100gCooked.fiber ?? null,
    salt: per100gCooked.salt ?? null,
    ingredients: incoming.map((i, position) => ({
      id: i.id ?? newId(),
      foodId: i.foodId,
      rawWeightG: i.rawWeightG,
      cooking: i.cooking ?? null,
      position,
    })),
    clientEditedAt: now(),
    deletedAt: null,
    dirty: 1,
  }
  await db.transaction('rw', [db.recipes, db.foods], async () => {
    await db.recipes.put(recipe)
    // Ingredients are user data. Evicting one would leave the recipe unable to
    // recompute its own macros the next time the cooked weight is corrected.
    await db.foods
      .where('id')
      .anyOf(recipe.ingredients.map((i) => i.foodId))
      .modify({ pinned: 1 })
  })
  return recipe
}

export async function deleteRecipe(id: string): Promise<void> {
  const recipe = await db.recipes.get(id)
  if (!recipe) return
  await db.recipes.put({ ...recipe, deletedAt: now(), clientEditedAt: now(), dirty: 1 })
}

/** Undo — see restoreEntry. */
export async function restoreRecipe(id: string): Promise<void> {
  const recipe = await db.recipes.get(id)
  if (!recipe) return
  await db.recipes.put({ ...recipe, deletedAt: null, clientEditedAt: now(), dirty: 1 })
}

export async function listRecipes(): Promise<LocalRecipe[]> {
  const rows = await db.recipes.toArray()
  return rows.filter((r) => r.deletedAt == null).sort((a, b) => a.name.localeCompare(b.name, 'lt'))
}

// ─── Favourites & usage ──────────────────────────────────────────────────────

/** A favourite points at exactly one of the three tables. */
function favouriteRef(fav: LocalFavourite): { kind: FavouriteKind; refId: string } | null {
  if (fav.foodId) return { kind: 'food', refId: fav.foodId }
  if (fav.recipeId) return { kind: 'recipe', refId: fav.recipeId }
  if (fav.buildId) return { kind: 'build', refId: fav.buildId }
  return null
}

export async function isFavourite(kind: FavouriteKind, refId: string): Promise<boolean> {
  return (await db.favourites.get(refKey(kind, refId))) != null
}

export async function addFavourite(kind: FavouriteKind, refId: string): Promise<void> {
  await db.transaction('rw', [db.favourites, db.foods], async () => {
    await db.favourites.put({
      id: refKey(kind, refId),
      foodId: kind === 'food' ? refId : null,
      recipeId: kind === 'recipe' ? refId : null,
      buildId: kind === 'build' ? refId : null,
      addedAt: now(),
    })
    if (kind === 'food') await db.foods.update(refId, { pinned: 1 })
  })
}

export async function removeFavourite(kind: FavouriteKind, refId: string): Promise<void> {
  await db.favourites.delete(refKey(kind, refId))
}

/** Returns the new state, which is what a star button needs back. */
export async function toggleFavourite(kind: FavouriteKind, refId: string): Promise<boolean> {
  if (await isFavourite(kind, refId)) {
    await removeFavourite(kind, refId)
    return false
  }
  await addFavourite(kind, refId)
  return true
}

/**
 * The same, for a catalogue row that may never have been on this phone.
 *
 * A search result and a browsed category row are not written to `db.foods` —
 * the app stores one only once it is chosen — so starring by id alone left a
 * favourite pointing at nothing: `addFavourite`'s pin is an update, which is
 * silently a no-op on a missing key, and the list read it back as absent. The
 * star is the choosing.
 */
export async function toggleFoodFavourite(food: LocalFood): Promise<boolean> {
  if (await isFavourite('food', food.id)) {
    await removeFavourite('food', food.id)
    return false
  }
  await cacheFood(food)
  await addFavourite('food', food.id)
  return true
}

export async function listFavourites(limit = 50): Promise<LocalFavourite[]> {
  return db.favourites.orderBy('addedAt').reverse().limit(limit).toArray()
}

/**
 * Count one use of a food, recipe or build.
 *
 * Kept out of the rows themselves: a counter bumped on every log would mark a
 * synced row dirty and push it back to the server for something the server has
 * no column for.
 */
export async function recordUsage(kind: FavouriteKind, refId: string): Promise<void> {
  const id = refKey(kind, refId)
  const existing = await db.usage.get(id)
  await db.usage.put({ id, kind, refId, lastUsedAt: now(), count: (existing?.count ?? 0) + 1 })
  // So the food just logged carries its weight in the very next search rather
  // than in fifteen seconds' time — which is exactly when somebody logging a
  // meal is most likely to search for the next thing in it.
  forgetFoodUsage()
}

export interface QuickAddItem {
  kind: FavouriteKind
  id: string
  name: string
  per100g: Per100g
  unit: 'g' | 'ml'
  /** The amount one of these normally is, when the label states one. */
  servingSizeG: number | null
  lastUsedAt: string
  count: number
  favourite: boolean
}

async function resolveUsage(rows: readonly LocalUsage[], limit: number): Promise<QuickAddItem[]> {
  const [foods, recipes, favourites] = await Promise.all([
    db.foods.bulkGet(rows.flatMap((r) => (r.kind === 'food' ? [r.refId] : []))),
    db.recipes.bulkGet(rows.flatMap((r) => (r.kind === 'recipe' ? [r.refId] : []))),
    db.favourites.bulkGet(rows.map((r) => r.id)),
  ])

  const named = new Map<
    string,
    { name: string; per100g: Per100g; unit: 'g' | 'ml'; servingSizeG: number | null }
  >()
  for (const food of foods) {
    if (food && food.deletedAt == null) {
      named.set(refKey('food', food.id), {
        name: foodDisplayName(food),
        per100g: foodPer100g(food),
        unit: food.unit,
        servingSizeG: food.servingSizeG,
      })
    }
  }
  for (const recipe of recipes) {
    if (recipe && recipe.deletedAt == null) {
      named.set(refKey('recipe', recipe.id), {
        name: recipe.name,
        per100g: foodPer100g(recipe),
        unit: 'g',
        // A recipe's portion is a decision made when it is eaten, not a
        // property of it — the cooked weight is the whole pot.
        servingSizeG: null,
      })
    }
  }
  const starred = new Set(favourites.flatMap((f) => (f ? [f.id] : [])))

  const items: QuickAddItem[] = []
  for (const row of rows) {
    const resolved = named.get(row.id)
    // Dropped rather than shown blank: the food may have been evicted, the
    // recipe deleted, and builds have no local table to resolve against yet.
    if (!resolved) continue
    items.push({
      kind: row.kind,
      id: row.refId,
      ...resolved,
      lastUsedAt: row.lastUsedAt,
      count: row.count,
      favourite: starred.has(row.id),
    })
    if (items.length === limit) break
  }
  return items
}

// Twice the asked-for number is read because some rows no longer resolve, and
// a quick-add list that comes back short is worse than one extra index read.
const USAGE_OVERSCAN = 2

export async function recentlyLogged(limit = 12): Promise<QuickAddItem[]> {
  const rows = await db.usage
    .orderBy('lastUsedAt')
    .reverse()
    .limit(limit * USAGE_OVERSCAN)
    .toArray()
  return resolveUsage(rows, limit)
}

/**
 * How far back "lately" reaches, and how many times makes a habit.
 *
 * Two months covers a shopping rhythm without letting a food somebody ate all
 * winter and gave up in March keep a place; three is the smallest number that
 * can distinguish a habit from having tried something and its leftovers.
 */
export const HABIT_WINDOW_DAYS = 60
export const HABIT_MIN_TIMES = 3

/**
 * The Produktai screen's opening list: what this person actually eats.
 *
 * Starred first, in the order they were starred, because that is a decision
 * somebody made on purpose. Under them, whatever the diary shows has been eaten
 * at least HABIT_MIN_TIMES in the last HABIT_WINDOW_DAYS, most often first —
 * which catches everything the star would have been used for and nobody
 * remembered to press.
 *
 * Counted from the diary rather than from `db.usage`, and not only because the
 * window needs dates: that counter is local to one phone, never decremented when
 * an entry is deleted, and wiped on an account change. Diary entries sync.
 */
export async function favouriteFoods(limit = 40): Promise<LocalFood[]> {
  const starred: string[] = []
  for (const fav of await db.favourites.orderBy('addedAt').reverse().toArray()) {
    if (fav.foodId) starred.push(fav.foodId)
  }

  const from = localDateString(new Date(Date.now() - HABIT_WINDOW_DAYS * DAY_MS))
  const counted = new Map<string, number>()
  // logDate is indexed and lexicographically ordered, so this reads the window
  // and not the diary. deletedAt and foodId are not, hence the filter in memory.
  await db.logEntries
    .where('logDate')
    .aboveOrEqual(from)
    .each((entry) => {
      if (entry.deletedAt != null || entry.foodId == null) return
      counted.set(entry.foodId, (counted.get(entry.foodId) ?? 0) + 1)
    })

  const already = new Set(starred)
  const often = [...counted]
    .filter(([id, times]) => times >= HABIT_MIN_TIMES && !already.has(id))
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => id)

  const wanted = [...starred, ...often].slice(0, limit * USAGE_OVERSCAN)
  const rows = await db.foods.bulkGet(wanted)
  // A row the cache has evicted resolves to nothing; dropping it is better than
  // a blank line, and a starred food is pinned so it cannot happen to those.
  return rows.filter((f): f is LocalFood => f != null && f.deletedAt == null).slice(0, limit)
}

export async function mostLogged(limit = 12): Promise<QuickAddItem[]> {
  const rows = await db.usage
    .orderBy('count')
    .reverse()
    .limit(limit * USAGE_OVERSCAN)
    .toArray()
  return resolveUsage(rows, limit)
}

export async function favouriteItems(limit = 24): Promise<QuickAddItem[]> {
  const favourites = await listFavourites(limit)
  const usage = await db.usage.bulkGet(favourites.map((f) => f.id))
  const byKey = new Map(usage.flatMap((u) => (u ? ([[u.id, u]] as const) : [])))

  const rows = favourites.flatMap((fav) => {
    const ref = favouriteRef(fav)
    if (!ref) return []
    const used = byKey.get(fav.id)
    return [
      {
        id: fav.id,
        ...ref,
        // A favourite that has never been logged still has to sort somewhere.
        lastUsedAt: used?.lastUsedAt ?? fav.addedAt,
        count: used?.count ?? 0,
      },
    ]
  })
  return resolveUsage(rows, limit)
}

/**
 * The pool the end-of-day suggester picks from.
 *
 * Favourites first, then whatever gets logged most often. Both lists resolve
 * against the local catalogue, so nothing in here is a food this phone could
 * not log at nine in the evening with no signal — which is the whole point of
 * suggesting from what someone already eats rather than from the catalogue.
 */
export async function fitPool(limit = 30): Promise<QuickAddItem[]> {
  const [starred, often] = await Promise.all([favouriteItems(limit), mostLogged(limit)])
  const seen = new Set<string>()
  return [...starred, ...often].filter((item) => {
    const key = refKey(item.kind, item.id)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

// ─── Goals & wearable ────────────────────────────────────────────────────────

export const DEFAULT_GOAL: Omit<LocalGoal, 'id' | 'clientEditedAt' | 'deletedAt' | 'dirty'> = {
  effectiveFrom: '1970-01-01',
  kcalTarget: 2000,
  proteinG: 150,
  carbsG: 200,
  fatG: 65,
  fiberG: 30,
  // Null so the EU reference intake applies and follows the calorie target;
  // an explicit number here would freeze the limit at 2 000 kcal for everyone.
  sugarsG: null,
  satFatG: null,
  saltG: null,
}

/**
 * One read of the goals table, answered for as many dates as you like.
 *
 * The table holds a handful of rows and `goalForDate` reads all of them, which
 * is free once and wasteful thirty times — and asking for a month of targets
 * at a stretch is exactly what the voice's mood window does.
 */
export async function goalPicker(): Promise<(date: string) => LocalGoal> {
  const rows = (await db.goals.toArray())
    .filter((g) => g.deletedAt == null)
    // Newest start first, and on the same start the newer edit — two devices
    // that both set a target for today leave two rows behind, and the tie used
    // to fall to whichever uuid sorted first.
    .sort(
      (a, b) =>
        b.effectiveFrom.localeCompare(a.effectiveFrom) ||
        b.clientEditedAt.localeCompare(a.clientEditedAt),
    )
  const fallback: LocalGoal = {
    ...DEFAULT_GOAL,
    id: 'default',
    clientEditedAt: now(),
    deletedAt: null,
    dirty: 0,
  }
  return (date) => rows.find((g) => g.effectiveFrom <= date) ?? fallback
}

/** The goal in force on a date — the latest one that had started by then. */
export async function goalForDate(date: string): Promise<LocalGoal> {
  return (await goalPicker())(date)
}

/**
 * Every goal that has ever been in force, oldest first.
 *
 * What the phase counter reads: a cut is a stretch of consecutive goals that
 * all imply one, and the only record of when it started is the effective date
 * on the first of them.
 */
export async function goalHistory(): Promise<LocalGoal[]> {
  const rows = await db.goals.toArray()
  return rows
    .filter((g) => g.deletedAt == null)
    .sort(
      (a, b) =>
        a.effectiveFrom.localeCompare(b.effectiveFrom) ||
        a.clientEditedAt.localeCompare(b.clientEditedAt),
    )
}

export async function saveGoal(
  goal: Omit<LocalGoal, 'id' | 'clientEditedAt' | 'deletedAt' | 'dirty'> & { id?: string },
): Promise<void> {
  await db.goals.put({
    ...goal,
    id: goal.id ?? newId(),
    clientEditedAt: now(),
    deletedAt: null,
    dirty: 1,
  })
}

export async function activeKcalFor(date: string): Promise<number | null> {
  const row = await db.wearableDaily.get(date)
  return row?.activeKcal ?? null
}

export async function setActiveKcal(date: string, activeKcal: number | null): Promise<void> {
  const existing = await db.wearableDaily.get(date)
  await db.wearableDaily.put({
    summaryDate: date,
    provider: existing?.provider ?? 'manual',
    activeKcal,
    restingKcal: existing?.restingKcal ?? null,
    steps: existing?.steps ?? null,
    dirty: 1,
  })
}

// ─── Body measurements ───────────────────────────────────────────────────────

export interface BodyMeasurementInput {
  measuredOn?: string
  weightKg?: number | null
  bodyFatPct?: number | null
  waistCm?: number | null
  note?: string | null
}

/** The live row for a day, if there is one. */
async function measurementOn(date: string): Promise<LocalBodyMeasurement | undefined> {
  const rows = await db.bodyMeasurements.where('measuredOn').equals(date).toArray()
  return rows.find((m) => m.deletedAt == null)
}

/**
 * Record a weigh-in.
 *
 * The day is the identity, not the row id. Someone who steps on the scale,
 * dislikes the number and steps on again has weighed themselves once, and a
 * second row would put a spike in a trend that exists to smooth exactly that
 * kind of noise away. The server keys `body_measurements` the same way, so the
 * two devices of one household collapse onto the same morning as well.
 */
export async function saveBodyMeasurement(
  input: BodyMeasurementInput,
): Promise<LocalBodyMeasurement> {
  const measuredOn = input.measuredOn ?? localDateString(new Date(), deviceTimeZone())
  return db.transaction('rw', db.bodyMeasurements, async () => {
    const existing = await measurementOn(measuredOn)
    // A scale may have written this day already. Its columns are carried over
    // untouched rather than rebuilt from the input, because this function
    // reconstructs the whole row: anything not named here would be dropped
    // locally and then pushed up as an erasure.
    const weightKg = patch(input.weightKg, existing?.weightKg)
    const row: LocalBodyMeasurement = {
      id: existing?.id ?? newId(),
      measuredOn,
      weightKg,
      bodyFatPct: patch(input.bodyFatPct, existing?.bodyFatPct),
      waistCm: patch(input.waistCm, existing?.waistCm),
      muscleKg: existing?.muscleKg ?? null,
      waterPct: existing?.waterPct ?? null,
      impedanceOhm: existing?.impedanceOhm ?? null,
      impedanceHighOhm: existing?.impedanceHighOhm ?? null,
      proteinPct: existing?.proteinPct ?? null,
      ecwL: existing?.ecwL ?? null,
      icwL: existing?.icwL ?? null,
      // Typing a different weight makes the row a person's statement rather
      // than the scale's, so the badge goes away. Correcting only the note or
      // the waist leaves the reading — and its provenance — alone.
      source: weightKg !== (existing?.weightKg ?? null) ? 'manual' : (existing?.source ?? 'manual'),
      note: patch(input.note, existing?.note),
      clientEditedAt: now(),
      deletedAt: null,
      dirty: 1,
    }
    // Mirrors the zod refine and the database CHECK: a row measuring nothing is
    // a sync artefact rather than a measurement.
    if (row.weightKg == null && row.bodyFatPct == null && row.waistCm == null) {
      throw new Error('Įrašui reikia bent vieno matavimo')
    }
    await db.bodyMeasurements.put(row)
    return row
  })
}

export async function deleteBodyMeasurement(id: string): Promise<void> {
  const row = await db.bodyMeasurements.get(id)
  if (!row) return
  await db.bodyMeasurements.put({ ...row, deletedAt: now(), clientEditedAt: now(), dirty: 1 })
}

/** Inclusive on both ends, oldest first — the shape the trend chart wants. */
export async function bodyMeasurementsBetween(
  from: string,
  to: string,
): Promise<LocalBodyMeasurement[]> {
  const rows = await db.bodyMeasurements.where('measuredOn').between(from, to, true, true).toArray()
  return rows
    .filter((m) => m.deletedAt == null)
    .sort((a, b) => a.measuredOn.localeCompare(b.measuredOn))
}

/** Latest weight on or before a date — what Mifflin-St Jeor needs as input. */
export async function latestWeightKg(on: string): Promise<number | null> {
  // Walked backwards and stopped at the first hit, so years of daily weigh-ins
  // cost one row to read rather than all of them.
  const row = await db.bodyMeasurements
    .where('measuredOn')
    .belowOrEqual(on)
    .reverse()
    .filter((m) => m.deletedAt == null && m.weightKg != null)
    .first()
  return row?.weightKg ?? null
}

/**
 * The most recent reading of each measurement, taken separately.
 *
 * Weight is written most mornings and a waist is written rarely, so the newest
 * row is usually weight-only. Asking each field for its own latest value is
 * what stops a daily weigh-in from erasing a waist measured last week — both
 * from the sheet, which prefills from this, and from the screens that show it.
 */
export interface LatestMeasurements {
  weightKg: number | null
  bodyFatPct: number | null
  waistCm: number | null
  muscleKg: number | null
  /**
   * Where the weight came from, but only when it was weighed on `on` itself.
   * A "the scale sent this morning's entry" hint over last Tuesday's number
   * would be a lie, so an older weight reports no source at all.
   */
  source: BodyMeasurementSource | undefined
  /** The day the weight above was taken; null when there is none. */
  weighedOn: string | null
}

export async function latestMeasurements(on: string): Promise<LatestMeasurements> {
  const rows = await db.bodyMeasurements
    .where('measuredOn')
    .belowOrEqual(on)
    .reverse()
    .filter((m) => m.deletedAt == null)
    .toArray()

  const newestRow = <K extends 'weightKg' | 'bodyFatPct' | 'waistCm' | 'muscleKg'>(key: K) =>
    rows.find((row) => row[key] != null)
  const newest = <K extends 'weightKg' | 'bodyFatPct' | 'waistCm' | 'muscleKg'>(
    key: K,
  ): number | null => newestRow(key)?.[key] ?? null

  const weightRow = newestRow('weightKg')
  return {
    weightKg: newest('weightKg'),
    bodyFatPct: newest('bodyFatPct'),
    waistCm: newest('waistCm'),
    muscleKg: newest('muscleKg'),
    source: weightRow?.measuredOn === on ? weightRow.source : undefined,
    weighedOn: weightRow?.measuredOn ?? null,
  }
}

// ─── Workouts ────────────────────────────────────────────────────────────────

export interface WorkoutInput {
  id?: string
  logDate?: string
  startedAt?: string | null
  kind?: LocalWorkout['kind']
  name?: string | null
  durationMin?: number | null
  kcal?: number | null
  distanceKm?: number | null
  avgHr?: number | null
  steps?: number | null
  source?: LocalWorkout['source']
  externalId?: string | null
  notes?: string | null
}

/**
 * Record a session.
 *
 * A row id wins when the caller has one, and an `externalId` is what catches
 * the rest: the band's exporter re-sends the same sessions nightly under ids it
 * invented, and matching them is what stops the week duplicating. Hand-entered
 * sessions carry no external id and stay distinct, which is deliberate — two
 * identical walks on the same day are two walks.
 */
export async function saveWorkout(input: WorkoutInput): Promise<LocalWorkout> {
  const source = input.source ?? 'manual'
  return db.transaction('rw', db.workouts, async () => {
    const byId = input.id ? await db.workouts.get(input.id) : undefined
    // The exporter's id is only unique within the exporter, which is why
    // `workouts_external_uq` is (user_id, source, external_id) and why the
    // source is matched here too: two bands are free to number their sessions
    // from one, and folding a run into a ride would lose both.
    const existing =
      byId ??
      (input.externalId
        ? (await db.workouts.where('externalId').equals(input.externalId).toArray()).find(
            (w) => w.deletedAt == null && w.source === source,
          )
        : undefined)

    const row: LocalWorkout = {
      id: existing?.id ?? input.id ?? newId(),
      // An edit correcting last Tuesday's kcal has no reason to restate the
      // date, and must not drag the session forward to today.
      logDate: input.logDate ?? existing?.logDate ?? localDateString(new Date(), deviceTimeZone()),
      startedAt: patch(input.startedAt, existing?.startedAt),
      kind: input.kind ?? existing?.kind ?? 'other',
      name: patch(input.name, existing?.name),
      durationMin: patch(input.durationMin, existing?.durationMin),
      kcal: patch(input.kcal, existing?.kcal),
      distanceKm: patch(input.distanceKm, existing?.distanceKm),
      avgHr: patch(input.avgHr, existing?.avgHr),
      steps: patch(input.steps, existing?.steps),
      source: input.source ?? existing?.source ?? 'manual',
      externalId: patch(input.externalId, existing?.externalId),
      notes: patch(input.notes, existing?.notes),
      clientEditedAt: now(),
      deletedAt: null,
      dirty: 1,
    }
    await db.workouts.put(row)
    return row
  })
}

export async function deleteWorkout(id: string): Promise<void> {
  const row = await db.workouts.get(id)
  if (!row) return
  await db.workouts.put({ ...row, deletedAt: now(), clientEditedAt: now(), dirty: 1 })
}

/** Undo — see restoreEntry. */
export async function restoreWorkout(id: string): Promise<void> {
  const row = await db.workouts.get(id)
  if (!row) return
  await db.workouts.put({ ...row, deletedAt: null, clientEditedAt: now(), dirty: 1 })
}

export async function workoutsForDay(date: string): Promise<LocalWorkout[]> {
  const rows = await db.workouts.where('logDate').equals(date).toArray()
  return rows.filter((w) => w.deletedAt == null)
}

// ─── Awards (Glitimė's trophy cabinet) ────────────────────────────────────────

export interface AwardInput {
  kind: AwardKind
  awardKey: string
  awardedOn?: string
  meta?: Record<string, string | number | boolean | null>
}

/**
 * Record a trophy or story beat, exactly once. `awardKey` is the trophy's own
 * stable id, and this device is not the only judge of "once": the same key
 * arriving from the server later collapses onto whichever row won there. An
 * award already on the phone wins here — `meta` freezes the figures the line
 * quoted, so it must never be rewritten after the fact.
 */
export async function grantAward(input: AwardInput): Promise<LocalAward | null> {
  return db.transaction('rw', db.awards, async () => {
    const existing = (await db.awards.where('awardKey').equals(input.awardKey).toArray()).find(
      (a) => a.deletedAt == null,
    )
    if (existing) return null
    const row: LocalAward = {
      id: newId(),
      kind: input.kind,
      awardKey: input.awardKey,
      awardedOn: input.awardedOn ?? localDateString(new Date(), deviceTimeZone()),
      meta: input.meta ?? {},
      clientEditedAt: now(),
      deletedAt: null,
      dirty: 1,
    }
    await db.awards.put(row)
    return row
  })
}

export async function hasAward(awardKey: string): Promise<boolean> {
  const rows = await db.awards.where('awardKey').equals(awardKey).toArray()
  return rows.some((a) => a.deletedAt == null)
}

/** Newest first — the cabinet reads like a feed, not a ledger. */
export async function listAwards(): Promise<LocalAward[]> {
  const rows = await db.awards.toArray()
  return rows
    .filter((a) => a.deletedAt == null)
    .sort((a, b) => b.awardedOn.localeCompare(a.awardedOn))
}

// ─── Water ───────────────────────────────────────────────────────────────────

/** A glass. Not a setting: nobody wants to configure the size of a glass. */
export const GLASS_ML = 250

/**
 * Water, logged as an ordinary entry rather than a counter of its own.
 *
 * It costs nothing — zero of everything — so it does not distort a single
 * total, and putting it in the diary means it syncs, edits and deletes through
 * exactly the machinery every other row already uses. A separate table would
 * have needed all of that again for a number that is only ever "one more glass".
 */
export async function addWater(logDate: string, ml: number = GLASS_ML): Promise<void> {
  await logAdHoc(
    WATER_NAME,
    // Zeros, not nulls. A null means "this food never said", and one such row
    // makes the whole day's sugar, saturated fat, fibre and salt unknown — so a
    // single glass of water used to blank all four meters for the rest of the
    // day. Water is the one thing in the diary we know the answer for.
    { kcal: 0, protein: 0, carbs: 0, fat: 0, sugars: 0, satFat: 0, fiber: 0, salt: 0 },
    { slot: 'snack', quantityG: ml, logDate },
  )
}

/** Which of the day's four secondary nutrients no label would commit to. */
export function unstatedFor(entries: readonly LocalLogEntry[]): NullableNutrient[] {
  return undeclaredNutrients(entries)
}

/** Undo the last glass, because the plus button will be pressed twice. */
export async function removeWater(logDate: string): Promise<void> {
  const glasses = (await db.logEntries.where('logDate').equals(logDate).toArray())
    .filter((entry) => entry.deletedAt == null && entry.snapshotName === WATER_NAME)
    .sort((a, b) => (a.loggedAt ?? '').localeCompare(b.loggedAt ?? ''))
  const last = glasses[glasses.length - 1]
  if (last) await deleteEntry(last.id)
}

// ─── Export ──────────────────────────────────────────────────────────────────

/** RFC 4180: quote everything that could be read as structure, double the quotes. */
function csvCell(value: unknown): string {
  if (value == null) return ''
  const text = String(value)
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

function toCsv(header: readonly string[], rows: ReadonlyArray<readonly unknown[]>): string {
  return [header.join(','), ...rows.map((row) => row.map(csvCell).join(','))].join('\n')
}

/**
 * The whole diary, as a file the user keeps.
 *
 * Years of two people's meals should never be hostage to this app still
 * running, to this VPS still being paid for, or to me still maintaining it —
 * and every log entry already carries its own frozen macros, so a flat row per
 * entry is the complete record rather than a set of pointers into a catalogue
 * that would have to be exported alongside it.
 */
export async function exportDiaryCsv(): Promise<string> {
  const entries = (await db.logEntries.toArray())
    .filter((e) => e.deletedAt == null)
    .sort(
      (a, b) =>
        a.logDate.localeCompare(b.logDate) || (a.loggedAt ?? '').localeCompare(b.loggedAt ?? ''),
    )
  return toCsv(
    [
      'data',
      'valgymas',
      'produktas',
      'kiekis_g',
      'porcijos',
      'kcal',
      'baltymai_g',
      'angliavandeniai_g',
      'riebalai_g',
      'cukrus_g',
      'sotieji_g',
      'skaidulos_g',
      'druska_g',
      'pastaba',
    ],
    entries.map((e) => [
      e.logDate,
      SLOT_LABEL[e.slot],
      e.snapshotName,
      e.quantityG,
      e.servings,
      e.kcal,
      e.protein,
      e.carbs,
      e.fat,
      e.sugars,
      e.satFat,
      e.fiber,
      e.salt,
      e.notes,
    ]),
  )
}

/** The scale's own history, which is the one measurement that is not an estimate. */
export async function exportMeasurementsCsv(): Promise<string> {
  const rows = (await db.bodyMeasurements.toArray())
    .filter((m) => m.deletedAt == null)
    .sort((a, b) => a.measuredOn.localeCompare(b.measuredOn))
  return toCsv(
    ['data', 'svoris_kg', 'riebalai_proc', 'juosmuo_cm', 'pastaba'],
    rows.map((m) => [m.measuredOn, m.weightKg, m.bodyFatPct, m.waistCm, m.note]),
  )
}
